import { CHOREOGRAPHY_DEFINITIONS, THEME_DEFINITIONS } from "./atlas.js";
import { deriveSeed } from "./seed.js";
import type { SeedSource } from "./seed.js";
import type {
  AffinityVector,
  AtlasChoreographyId,
  ChoreographyScore,
  InkIntensityProfile,
  ThemeId,
  ThemeMaterial,
} from "../experience/types.js";

export const SCENE_PLAN_NAMESPACE = "scene/v1" as const;

export interface ScenePrimitiveSemantics {
  readonly coordinateSystem: "normalized-viewport";
  readonly streakXRange: readonly [-0.1, 1.1];
  readonly rippleCoordinateRange: readonly [0, 1];
  readonly distanceUnit: "css-px-at-dpr-1";
  readonly speedUnit: "css-px-per-second";
  readonly timeUnit: "milliseconds";
  readonly phaseRange: "zero-inclusive-one-exclusive";
  readonly slantUnit: "normalized-x-per-normalized-y";
  readonly viewportMapping: "adapter-owned-resource-behavior";
}

/**
 * Renderer-neutral primitive units. Adapters map normalized coordinates and
 * CSS-pixel distances into their own viewport/world/DPR resources; that
 * mapping does not add visual authority or alter the signed ScenePlan.
 */
export const SCENE_PRIMITIVE_SEMANTICS: ScenePrimitiveSemantics = Object.freeze({
  coordinateSystem: "normalized-viewport",
  streakXRange: Object.freeze([-0.1, 1.1] as const),
  rippleCoordinateRange: Object.freeze([0, 1] as const),
  distanceUnit: "css-px-at-dpr-1",
  speedUnit: "css-px-per-second",
  timeUnit: "milliseconds",
  phaseRange: "zero-inclusive-one-exclusive",
  slantUnit: "normalized-x-per-normalized-y",
  viewportMapping: "adapter-owned-resource-behavior",
});

export interface SceneAmbienceInput {
  readonly breeze: number;
  readonly rainBias: number;
}

export interface SceneAmbience {
  readonly breeze: number;
  readonly rainBias: number;
  readonly slantDelta: number;
  readonly densityScale: number;
}

export interface SceneStreakPrimitive {
  /** Normalized viewport x, including the signed rain overscan range. */
  readonly x: number;
  /** Unit interval [0, 1), interpreted as an animation-cycle phase. */
  readonly phase: number;
  /** CSS pixels per second, independent of device-pixel ratio. */
  readonly speed: number;
  /** CSS pixels at DPR 1. */
  readonly length: number;
  /** Unitless opacity in [0, 1]. */
  readonly alpha: number;
}

export interface SceneRipplePrimitive {
  /** Normalized viewport coordinates in [0, 1). */
  readonly x: number;
  readonly y: number;
  /** Unit interval [0, 1), interpreted as a lifetime phase. */
  readonly phase: number;
  /** CSS pixels at DPR 1. */
  readonly radius: number;
  /** Unitless opacity in [0, 1]. */
  readonly alpha: number;
  /** Lifetime in milliseconds. */
  readonly lifeMs: number;
}

export interface ScenePlanUnsigned {
  readonly version: 1;
  readonly seed: number;
  readonly theme: {
    readonly id: ThemeId;
  };
  readonly choreography: {
    readonly id: AtlasChoreographyId;
    readonly intensity: number;
    readonly parallax: number;
    readonly particleDensity: number;
    readonly drift: number;
    readonly spread: number;
  };
  readonly animation: {
    readonly durationMs: number;
    readonly staggerMs: number;
  };
  readonly palette: {
    readonly background: string;
    readonly ink: string;
    readonly wash: string;
    readonly accent: string;
  };
  readonly wash: {
    readonly color: string;
    readonly opacity: number;
    readonly coverage: number;
  };
  readonly rain: {
    /** Signed normalized x bounds; adapters map these through their viewport resources. */
    readonly overscan: readonly [-0.1, 1.1];
    readonly speedRange: readonly [number, number];
    /** Slope: normalized horizontal displacement per one normalized vertical unit. */
    readonly slant: number;
    readonly streakLengthRange: readonly [number, number];
    readonly streakAlphaRange: readonly [number, number];
    readonly primitives: readonly SceneStreakPrimitive[];
  };
  readonly ripples: {
    readonly radiusRange: readonly [number, number];
    readonly primitives: readonly SceneRipplePrimitive[];
  };
  readonly paper: {
    readonly color: string;
    readonly grain: number;
  };
  readonly ambience: SceneAmbience;
}

declare const scenePlanSignatureBrand: unique symbol;

export type ScenePlanSignature = string & {
  readonly [scenePlanSignatureBrand]: "ScenePlanSignature";
};

const SCENE_PLAN_SIGNATURE_PATTERN = /^scene\/v1:[0-9a-f]{32}$/;

export function parseScenePlanSignature(value: unknown): ScenePlanSignature {
  if (typeof value !== "string" || !SCENE_PLAN_SIGNATURE_PATTERN.test(value)) {
    throw new TypeError("Invalid ScenePlan signature");
  }
  return value as ScenePlanSignature;
}

export interface ScenePlan extends ScenePlanUnsigned {
  readonly signature: ScenePlanSignature;
}

export interface ScenePlanInput {
  readonly seed: SeedSource;
  readonly themeId: ThemeId;
  readonly choreographyId: AtlasChoreographyId;
  readonly ambience?: SceneAmbienceInput;
  readonly visual?: ScenePlanVisualInput;
}

/** Compiler-only semantic input. None of these fields extend the signed v1 wire shape. */
export interface ScenePlanVisualInput {
  readonly theme: ThemeMaterial;
  readonly choreography: ChoreographyScore;
  readonly affinity: AffinityVector;
  readonly intensity: InkIntensityProfile;
}

const SPEED_RANGE = Object.freeze([700, 1300] as const);
const BASE_SLANT = 0.16;
const STREAK_X_RANGE = SCENE_PRIMITIVE_SEMANTICS.streakXRange;
const STREAK_LENGTH_RANGE = Object.freeze([14.4, 33.6] as const);
const STREAK_ALPHA_RANGE = Object.freeze([0.14, 0.28] as const);
const RIPPLE_RADIUS_RANGE = Object.freeze([60, 100] as const);
const BASE_STREAK_COUNT = 90;
const RIPPLE_PRIMITIVE_COUNT = 16;
const UINT32_RANGE = 0x1_0000_0000;
const FNV128_OFFSET_BASIS = 0x6c62272e07bb014262b821756295c58dn;
const FNV128_PRIME = 0x0000000001000000000000000000013bn;
const UINT128_MASK = (1n << 128n) - 1n;
const SAFE_COLOR = /^#[0-9a-f]{6}$/i;
const AFFINITY_KEYS = Object.freeze([
  "light", "warmth", "moisture", "weight", "fluidity", "turbulence", "resonance", "tension",
] as const);
const INTENSITY_IDS = Object.freeze(["quiet", "present", "immersive"] as const);
const WORD_EXPRESSIONS = Object.freeze(["atmospheric", "material", "performative"] as const);

function deepFreeze<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

function clamp(value: number, minimum: number, maximum: number, nanFallback: number): number {
  if (Number.isNaN(value)) return nanFallback;
  return Math.min(maximum, Math.max(minimum, value));
}

function roundScalar(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function composeParticleDensity(baseDensity: number, densityScale: number): number {
  const base = clamp(baseDensity, 0, 1, 0);
  const scale = clamp(densityScale, 0, 2, 0);
  if (base === 0 || scale === 0) return 0;
  if (base === 1) return 1;
  return base * scale / (1 - base + base * scale);
}

function parseColor(value: string): readonly [number, number, number] {
  const normalized = SAFE_COLOR.test(value) ? value.slice(1) : "000000";
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function mixColor(from: string, to: string, amount: number): string {
  const start = parseColor(from);
  const end = parseColor(to);
  const bounded = clamp(amount, 0, 1, 0);
  return `#${start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * bounded).toString(16).padStart(2, "0")
  ).join("")}`;
}

function shiftAccent(color: string, affinity: AffinityVector): string {
  const channels = parseColor(color);
  const shifted = [
    channels[0] + affinity.warmth * 18 + affinity.light * 8,
    channels[1] + affinity.light * 8 - affinity.tension * 5,
    channels[2] + affinity.moisture * 14 - affinity.warmth * 6,
  ];
  return `#${shifted.map((channel) =>
    Math.round(clamp(channel, 0, 255, 0)).toString(16).padStart(2, "0")
  ).join("")}`;
}

function unit(seed: number, index: number): number {
  let value = (seed + Math.imul(index + 1, 0x9e3779b9)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x21f0aaad) >>> 0;
  value ^= value >>> 15;
  value = Math.imul(value, 0x735a2d97) >>> 0;
  value ^= value >>> 15;
  return (value >>> 0) / UINT32_RANGE;
}

export function deriveSceneSeed(source: SeedSource): number {
  if (typeof source === "number" && !Number.isFinite(source)) {
    throw new RangeError("Numeric seed source must be finite");
  }
  const identity = typeof source === "number" ? `number:${source}` : `string:${source}`;
  return deriveSeed(SCENE_PLAN_NAMESPACE, identity);
}

function derivePrimitiveStreamSeed(sceneSeed: number, stream: "streaks" | "ripples"): number {
  return deriveSeed(SCENE_PLAN_NAMESPACE, `scene-seed:${sceneSeed}\0primitive-stream:${stream}/v1`);
}

function between(range: readonly [number, number], sample: number): number {
  return roundScalar(range[0] + (range[1] - range[0]) * sample);
}

function resolveAmbience(input: SceneAmbienceInput | undefined): SceneAmbience {
  const breeze = clamp(input?.breeze ?? 0, -1, 1, 0);
  const rainBias = clamp(input?.rainBias ?? 0, 0, 1, 0);
  return deepFreeze({
    breeze,
    rainBias,
    slantDelta: roundScalar(breeze * 0.12),
    densityScale: roundScalar(1 + rainBias * 0.6),
  });
}

function resolveStreaks(
  sceneSeed: number,
  count: number,
  speedRange: readonly [number, number] = SPEED_RANGE,
  lengthRange: readonly [number, number] = STREAK_LENGTH_RANGE,
  alphaRange: readonly [number, number] = STREAK_ALPHA_RANGE,
): readonly SceneStreakPrimitive[] {
  const seed = derivePrimitiveStreamSeed(sceneSeed, "streaks");
  return deepFreeze(Array.from({ length: count }, (_, index) => {
    const offset = index * 5;
    return {
      x: between(STREAK_X_RANGE, unit(seed, offset)),
      phase: roundScalar(unit(seed, offset + 1)),
      speed: between(speedRange, unit(seed, offset + 2)),
      length: between(lengthRange, unit(seed, offset + 3)),
      alpha: between(alphaRange, unit(seed, offset + 4)),
    };
  }));
}

function resolveRipples(
  sceneSeed: number,
  radiusRange: readonly [number, number] = RIPPLE_RADIUS_RANGE,
  alpha = 0.35,
  lifeMs = 1000,
): readonly SceneRipplePrimitive[] {
  const seed = derivePrimitiveStreamSeed(sceneSeed, "ripples");
  return deepFreeze(Array.from({ length: RIPPLE_PRIMITIVE_COUNT }, (_, index) => {
    const offset = index * 4;
    return {
      x: roundScalar(unit(seed, offset)),
      y: roundScalar(unit(seed, offset + 1)),
      phase: roundScalar(unit(seed, offset + 2)),
      radius: between(radiusRange, unit(seed, offset + 3)),
      alpha,
      lifeMs,
    };
  }));
}

function canonicalJsonError(): TypeError {
  return new TypeError("ScenePlan canonical JSON accepts only finite plain JSON data properties");
}

function canonicalJsonCycleError(): TypeError {
  return new TypeError("ScenePlan canonical JSON rejects cyclic references");
}

function quoted(value: string): string {
  const encoded = JSON.stringify(value);
  if (encoded === undefined) throw canonicalJsonError();
  return encoded;
}

function canonicalJson(value: unknown, ancestors: Set<object> = new Set()): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string") return quoted(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw canonicalJsonError();
    return Object.is(value, -0) ? "0" : String(value);
  }
  if (typeof value !== "object") throw canonicalJsonError();
  if (ancestors.has(value)) throw canonicalJsonCycleError();
  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      const expectedKeys = [...Array.from({ length: value.length }, (_, index) => String(index)), "length"];
      const actualKeys = Reflect.ownKeys(value);
      if (
        actualKeys.length !== expectedKeys.length
        || actualKeys.some((key) => typeof key !== "string" || !expectedKeys.includes(key))
      ) {
        throw canonicalJsonError();
      }
      const values: string[] = [];
      for (let index = 0; index < value.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
        if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
          throw canonicalJsonError();
        }
        values.push(canonicalJson(descriptor.value, ancestors));
      }
      return `[${values.join(",")}]`;
    }
    const prototype: unknown = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) throw canonicalJsonError();
    const entries: Array<readonly [string, unknown]> = [];
    for (const key of Reflect.ownKeys(value)) {
      if (typeof key !== "string") throw canonicalJsonError();
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
        throw canonicalJsonError();
      }
      entries.push([key, descriptor.value]);
    }
    entries.sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0);
    return `{${entries.map(([key, nested]) =>
      `${quoted(key)}:${canonicalJson(nested, ancestors)}`
    ).join(",")}}`;
  } finally {
    ancestors.delete(value);
  }
}

function hashCanonicalPlan(canonicalPlan: string): string {
  const bytes = new TextEncoder().encode(`${SCENE_PLAN_NAMESPACE}\0${canonicalPlan}`);
  let hash = FNV128_OFFSET_BASIS;
  for (const byte of bytes) hash = ((hash ^ BigInt(byte)) * FNV128_PRIME) & UINT128_MASK;
  return hash.toString(16).padStart(32, "0");
}

export function computeScenePlanSignature(plan: ScenePlanUnsigned): ScenePlanSignature {
  return parseScenePlanSignature(`${SCENE_PLAN_NAMESPACE}:${hashCanonicalPlan(canonicalJson(plan))}`);
}

function contractError(label: string): never {
  throw new TypeError(`Invalid ScenePlan v1: ${label}`);
}

function exactRecord(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) contractError(`${label} must be plain`);
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) contractError(`${label} must be plain`);
  const actual = Reflect.ownKeys(value);
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key) => typeof key !== "string")
      || (actual as string[]).sort().some((key, index) => key !== expected[index])) {
    contractError(`${label} has an incomplete or extended schema`);
  }
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      contractError(`${label}.${key} must be an enumerable data property`);
    }
  }
  return value as Record<string, unknown>;
}

function denseArray(value: unknown, minimum: number, maximum: number, label: string): readonly unknown[] {
  if (!Array.isArray(value) || value.length < minimum || value.length > maximum) {
    contractError(`${label} has an invalid primitive count`);
  }
  const expected = [...Array.from({ length: value.length }, (_, index) => String(index)), "length"];
  const actual = Reflect.ownKeys(value);
  if (actual.length !== expected.length
      || actual.some((key) => typeof key !== "string" || !expected.includes(key))) {
    contractError(`${label} must be a dense unextended array`);
  }
  return value;
}

function finite(value: unknown, minimum: number, maximum: number, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    contractError(`${label} is outside [${minimum}, ${maximum}]`);
  }
  return value;
}

function phase(value: unknown, label: string): number {
  const number = finite(value, 0, 1, label);
  if (number === 1) contractError(`${label} must be below 1`);
  return number;
}

function text(value: unknown, label: string): string {
  if (typeof value !== "string") contractError(`${label} must be a string`);
  return value;
}

function color(value: unknown, label: string): string {
  const candidate = text(value, label);
  if (!SAFE_COLOR.test(candidate)) contractError(`${label} must be a safe six-digit hex color`);
  return candidate;
}

function tuple(
  value: unknown,
  minimum: number,
  maximum: number,
  label: string,
  positive = false,
): readonly [number, number] {
  const values = denseArray(value, 2, 2, label);
  const lower = finite(values[0], minimum, maximum, `${label}[0]`);
  const upper = finite(values[1], minimum, maximum, `${label}[1]`);
  if (positive && lower <= 0) contractError(`${label}[0] must be positive`);
  if (lower > upper) contractError(`${label} must be ordered`);
  return values as readonly [number, number];
}

function assertAffinity(value: unknown, label: string, bound: number): AffinityVector {
  const record = exactRecord(value, AFFINITY_KEYS, label);
  for (const key of AFFINITY_KEYS) finite(record[key], -bound, bound, `${label}.${key}`);
  return value as AffinityVector;
}

function assertVisualInput(
  visual: ScenePlanVisualInput,
  themeId: ThemeId,
  choreographyId: AtlasChoreographyId,
): void {
  const root = exactRecord(visual, ["affinity", "choreography", "intensity", "theme"], "visual");
  const theme = exactRecord(
    root.theme,
    ["accent", "affinityScale", "background", "grain", "id", "ink", "paper", "wash"],
    "visual.theme",
  );
  if (theme.id !== themeId) contractError("visual.theme.id does not match themeId");
  for (const key of ["background", "paper", "ink", "wash", "accent"] as const) {
    color(theme[key], `visual.theme.${key}`);
  }
  finite(theme.grain, 0, 1, "visual.theme.grain");
  assertAffinity(theme.affinityScale, "visual.theme.affinityScale", 2);

  const choreography = exactRecord(
    root.choreography,
    ["id", "intensity", "parallax", "particleDensity", "wordExpression"],
    "visual.choreography",
  );
  if (choreography.id !== choreographyId) {
    contractError("visual.choreography.id does not match choreographyId");
  }
  for (const key of ["intensity", "parallax", "particleDensity"] as const) {
    finite(choreography[key], 0, 1, `visual.choreography.${key}`);
  }
  if (!WORD_EXPRESSIONS.includes(text(
    choreography.wordExpression,
    "visual.choreography.wordExpression",
  ) as ChoreographyScore["wordExpression"])) {
    contractError("visual.choreography.wordExpression is unsupported");
  }

  assertAffinity(root.affinity, "visual.affinity", 1);
  const intensity = exactRecord(
    root.intensity,
    [
      "densityScale", "depthScale", "id", "motionScale", "parallaxScale", "particleScale",
      "washCoverage", "washFeather", "washOpacity", "washTone",
    ],
    "visual.intensity",
  );
  if (!INTENSITY_IDS.includes(text(intensity.id, "visual.intensity.id") as InkIntensityProfile["id"])) {
    contractError("visual.intensity.id is unsupported");
  }
  finite(intensity.washOpacity, 0, 1, "visual.intensity.washOpacity");
  finite(intensity.washCoverage, 0.25, 2, "visual.intensity.washCoverage");
  finite(intensity.washFeather, 0.01, 1, "visual.intensity.washFeather");
  finite(intensity.washTone, 0, 1, "visual.intensity.washTone");
  for (const key of ["densityScale", "depthScale", "parallaxScale"] as const) {
    finite(intensity[key], 0, 3, `visual.intensity.${key}`);
  }
  for (const key of ["particleScale", "motionScale"] as const) {
    finite(intensity[key], Number.MIN_VALUE, 3, `visual.intensity.${key}`);
  }
}

/** The single runtime authority for the exact signed Task 2.1 ScenePlan v1 contract. */
export function assertScenePlanV1(value: unknown): ScenePlan {
  const plan = exactRecord(value, [
    "ambience", "animation", "choreography", "palette", "paper", "rain", "ripples", "seed",
    "signature", "theme", "version", "wash",
  ], "ScenePlan");
  if (plan.version !== 1) contractError("version must be 1");
  const seed = finite(plan.seed, 0, 0xffff_ffff, "seed");
  if (!Number.isInteger(seed)) contractError("seed must be a uint32");

  const theme = exactRecord(plan.theme, ["id"], "theme");
  const themeId = text(theme.id, "theme.id");
  if (!THEME_DEFINITIONS.some((definition) => definition.id === themeId)) contractError("theme.id is unknown");

  const choreography = exactRecord(
    plan.choreography,
    ["drift", "id", "intensity", "parallax", "particleDensity", "spread"],
    "choreography",
  );
  const choreographyId = text(choreography.id, "choreography.id");
  if (!CHOREOGRAPHY_DEFINITIONS.some((definition) => definition.id === choreographyId)) {
    contractError("choreography.id is unknown");
  }
  finite(choreography.intensity, 0, 1, "choreography.intensity");
  finite(choreography.parallax, 0, 1, "choreography.parallax");
  finite(choreography.particleDensity, 0, 1, "choreography.particleDensity");
  finite(choreography.drift, 0, 1, "choreography.drift");
  finite(choreography.spread, 0, 2, "choreography.spread");

  const animation = exactRecord(plan.animation, ["durationMs", "staggerMs"], "animation");
  finite(animation.durationMs, Number.MIN_VALUE, 120_000, "animation.durationMs");
  finite(animation.staggerMs, 0, 10_000, "animation.staggerMs");

  const palette = exactRecord(plan.palette, ["accent", "background", "ink", "wash"], "palette");
  for (const key of ["background", "ink", "wash", "accent"] as const) color(palette[key], `palette.${key}`);
  const wash = exactRecord(plan.wash, ["color", "coverage", "opacity"], "wash");
  color(wash.color, "wash.color");
  finite(wash.opacity, 0, 1, "wash.opacity");
  finite(wash.coverage, Number.MIN_VALUE, 2, "wash.coverage");
  const paper = exactRecord(plan.paper, ["color", "grain"], "paper");
  color(paper.color, "paper.color");
  finite(paper.grain, 0, 1, "paper.grain");

  const ambience = exactRecord(plan.ambience, ["breeze", "densityScale", "rainBias", "slantDelta"], "ambience");
  finite(ambience.breeze, -1, 1, "ambience.breeze");
  finite(ambience.rainBias, 0, 1, "ambience.rainBias");
  finite(ambience.slantDelta, -1, 1, "ambience.slantDelta");
  finite(ambience.densityScale, 0, 2, "ambience.densityScale");

  const rain = exactRecord(plan.rain, [
    "overscan", "primitives", "slant", "speedRange", "streakAlphaRange", "streakLengthRange",
  ], "rain");
  const overscan = tuple(rain.overscan, -1, 2, "rain.overscan");
  if (overscan[0] !== -0.1 || overscan[1] !== 1.1) contractError("rain.overscan is unsupported");
  const speedRange = tuple(rain.speedRange, 1, 10_000, "rain.speedRange", true);
  const lengthRange = tuple(rain.streakLengthRange, 0.25, 4096, "rain.streakLengthRange", true);
  const alphaRange = tuple(rain.streakAlphaRange, 0, 1, "rain.streakAlphaRange");
  finite(rain.slant, -4, 4, "rain.slant");
  const streaks = denseArray(rain.primitives, 1, 512, "rain.primitives");
  for (const [index, primitive] of streaks.entries()) {
    const streak = exactRecord(primitive, ["alpha", "length", "phase", "speed", "x"], `rain.primitives[${index}]`);
    finite(streak.x, overscan[0], overscan[1], `rain.primitives[${index}].x`);
    phase(streak.phase, `rain.primitives[${index}].phase`);
    finite(streak.speed, speedRange[0], speedRange[1], `rain.primitives[${index}].speed`);
    finite(streak.length, lengthRange[0], lengthRange[1], `rain.primitives[${index}].length`);
    finite(streak.alpha, alphaRange[0], alphaRange[1], `rain.primitives[${index}].alpha`);
  }

  const ripples = exactRecord(plan.ripples, ["primitives", "radiusRange"], "ripples");
  const radiusRange = tuple(ripples.radiusRange, 0.25, 4096, "ripples.radiusRange", true);
  const ripplePrimitives = denseArray(ripples.primitives, 1, 64, "ripples.primitives");
  for (const [index, primitive] of ripplePrimitives.entries()) {
    const ripple = exactRecord(
      primitive,
      ["alpha", "lifeMs", "phase", "radius", "x", "y"],
      `ripples.primitives[${index}]`,
    );
    phase(ripple.x, `ripples.primitives[${index}].x`);
    phase(ripple.y, `ripples.primitives[${index}].y`);
    phase(ripple.phase, `ripples.primitives[${index}].phase`);
    finite(ripple.radius, radiusRange[0], radiusRange[1], `ripples.primitives[${index}].radius`);
    finite(ripple.alpha, 0, 1, `ripples.primitives[${index}].alpha`);
    finite(ripple.lifeMs, 16, 120_000, `ripples.primitives[${index}].lifeMs`);
  }

  const signature = parseScenePlanSignature(plan.signature);
  const { signature: _signature, ...unsigned } = plan;
  if (computeScenePlanSignature(unsigned as unknown as ScenePlanUnsigned) !== signature) {
    contractError("signature does not match the complete plan");
  }
  return value as ScenePlan;
}

export function resolveScenePlan(input: ScenePlanInput): ScenePlan {
  const themeDefinition = THEME_DEFINITIONS.find((candidate) => candidate.id === input.themeId);
  if (themeDefinition === undefined) throw new RangeError(`Unknown scene theme: ${String(input.themeId)}`);
  const choreographyDefinition = CHOREOGRAPHY_DEFINITIONS.find(
    (candidate) => candidate.id === input.choreographyId,
  );
  if (choreographyDefinition === undefined) {
    throw new RangeError(`Unknown scene choreography: ${String(input.choreographyId)}`);
  }

  if (input.visual !== undefined) {
    assertVisualInput(input.visual, input.themeId, input.choreographyId);
  }
  const visual = input.visual;
  const theme = visual?.theme ?? themeDefinition;
  const choreography = visual?.choreography ?? choreographyDefinition;
  const affinity = visual?.affinity;
  const intensity = visual?.intensity;

  const seed = deriveSceneSeed(input.seed);
  const ambience = resolveAmbience(input.ambience);
  const moisture = affinity?.moisture ?? 0;
  const resonance = affinity?.resonance ?? 0;
  const fluidity = affinity?.fluidity ?? 0;
  const turbulence = affinity?.turbulence ?? 0;
  const tension = affinity?.tension ?? 0;
  const light = affinity?.light ?? 0;
  const warmth = affinity?.warmth ?? 0;
  const weight = affinity?.weight ?? 0;
  const particleOpacity = clamp(0.7 + resonance * 0.15 + moisture * 0.05, 0.3, 1, 0.7);
  const speedScale = (intensity?.motionScale ?? 1) * (1 + fluidity * 0.05);
  const lengthScale = (intensity?.particleScale ?? 1) * (1 + weight * 0.05);
  const alphaScale = particleOpacity / 0.7;
  const speedRange = visual === undefined
    ? SPEED_RANGE
    : Object.freeze(SPEED_RANGE.map((value) => Math.max(1, value * speedScale)) as [number, number]);
  const lengthRange = visual === undefined
    ? STREAK_LENGTH_RANGE
    : Object.freeze(STREAK_LENGTH_RANGE.map((value) => Math.max(0.25, value * lengthScale)) as [number, number]);
  const alphaRange = visual === undefined
    ? STREAK_ALPHA_RANGE
    : Object.freeze(STREAK_ALPHA_RANGE.map((value) => value * alphaScale) as [number, number]);
  const washCoverage = visual === undefined
    ? roundScalar(0.75 + choreographyDefinition.spread * 0.5)
    : clamp(intensity!.washCoverage + moisture * 0.05, 0.25, 2, intensity!.washCoverage);
  const inkOpacity = clamp(0.16 + moisture * 0.05 + tension * 0.04, 0.08, 0.32, 0.16);
  const washOpacity = visual === undefined
    ? roundScalar(0.12 + choreographyDefinition.intensity * 0.24)
    : roundScalar(clamp(inkOpacity * intensity!.washOpacity / 0.16, 0.08, 0.58, intensity!.washOpacity));
  const rippleRadiusRange = visual === undefined
    ? RIPPLE_RADIUS_RANGE
    : Object.freeze(RIPPLE_RADIUS_RANGE.map((value) => roundScalar(value * intensity!.washCoverage)) as [number, number]);
  const particleDensity = composeParticleDensity(
    choreography.particleDensity,
    intensity?.densityScale ?? 1,
  );
  const streakCount = Math.max(
    1,
    Math.round(BASE_STREAK_COUNT * particleDensity * ambience.densityScale),
  );
  const unsigned: ScenePlanUnsigned = deepFreeze({
    version: 1,
    seed,
    theme: { id: theme.id },
    choreography: {
      id: choreography.id,
      intensity: choreography.intensity,
      parallax: choreography.parallax,
      particleDensity: choreography.particleDensity,
      drift: visual === undefined
        ? choreographyDefinition.drift
        : clamp(choreographyDefinition.drift + turbulence * 0.1, 0, 1, choreographyDefinition.drift),
      spread: visual === undefined
        ? choreographyDefinition.spread
        : clamp(choreographyDefinition.spread + tension * 0.05, 0, 2, choreographyDefinition.spread),
    },
    animation: {
      durationMs: visual === undefined
        ? choreographyDefinition.durationMs
        : Math.min(
          120_000,
          roundScalar(choreographyDefinition.durationMs / clamp(intensity!.motionScale, 0.01, 3, 1)),
        ),
      staggerMs: visual === undefined
        ? choreographyDefinition.staggerMs
        : clamp(choreographyDefinition.staggerMs + warmth * 10, 0, 10_000, choreographyDefinition.staggerMs),
    },
    palette: {
      background: theme.background,
      ink: theme.ink,
      wash: theme.wash,
      accent: visual === undefined ? theme.accent : shiftAccent(theme.accent, affinity!),
    },
    wash: {
      color: visual === undefined ? theme.wash : mixColor(theme.ink, theme.wash, intensity!.washTone),
      opacity: washOpacity,
      coverage: washCoverage,
    },
    rain: {
      overscan: STREAK_X_RANGE,
      speedRange,
      slant: visual === undefined
        ? roundScalar(BASE_SLANT + ambience.slantDelta)
        : BASE_SLANT + ambience.slantDelta + fluidity * 0.01,
      streakLengthRange: lengthRange,
      streakAlphaRange: alphaRange,
      primitives: resolveStreaks(seed, streakCount, speedRange, lengthRange, alphaRange),
    },
    ripples: {
      radiusRange: rippleRadiusRange,
      primitives: resolveRipples(
        seed,
        rippleRadiusRange,
        visual === undefined ? 0.35 : washOpacity,
        visual === undefined ? 1000 : roundScalar(1000 / clamp(intensity!.motionScale, 0.01, 3, 1)),
      ),
    },
    paper: {
      color: theme.paper,
      grain: visual === undefined
        ? theme.grain
        : clamp(theme.grain + light * 0.05, 0, 1, theme.grain),
    },
    ambience,
  });
  const signed = { ...unsigned, signature: computeScenePlanSignature(unsigned) } as ScenePlan;
  assertScenePlanV1(signed);
  return deepFreeze(signed);
}
